import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "react-toastify";
import {
    addressHiddenTooltip,
    importBlockedTooltip,
    isAddressHidden,
    isImportBlocked,
} from "../../utils/mlsCandidateImport";
import { duplicateHint } from "../../utils/mlsCandidateGrouping";
import { Home, Inbox, Info, Loader2, MapPin, RotateCcw, Trash2, X } from "lucide-react";
import axios from "axios";
// Side-effect import so the axios singleton carries the Gandalf/impersonation/401
// interceptors regardless of import ordering (same rationale as mlsCandidatesApi).
import "../../utils/axiosConfig";
import CustomToast from "../misc/CustomToast";
import { Button } from "../misc/Button";
import Checkbox from "../misc/Checkbox";
import ConfirmPopup from "../misc/ConfirmPopup";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";
import SimpleTooltip from "../misc/SimpleTooltip";
import { Dialog, DialogContent, DialogTitle } from "../misc/Dialog";
import { Tabs, TabsList, TabsTrigger } from "../misc/Tabs";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from "../misc/Pagination";
import {
    addMlsCandidates,
    discardMlsCandidates,
    getMlsCandidates,
    refreshMlsCandidates,
    restoreMlsCandidates,
} from "./mlsCandidatesApi";
import {
    getMlsPropertyTypes,
    updateMlsPropertyTypes,
} from "./mlsPropertyTypesApi";
import {
    keepKnownKeys,
    keysToRestore,
    SETUP_STEP_LABELS,
    stepAfterListings,
    stepAfterTypes,
    stepBeforeAutopilot,
} from "../../utils/mlsSetupWizardSteps";
import {
    MlsCandidateSnapshot,
    MlsListingCandidate,
} from "../../types/mlsCandidate";
import { MlsPropertyTypeOption } from "../../types/mlsPropertyTypes";
import { AllListings } from "../../types/allListings";
import { getAuthHeader } from "../../utils/authHeader";
import { publishAutopilotEnabled } from "../../hooks/useAutopilotEnabled";
import MlsDetails from "../settings/MlsDetails/MlsDetails";
import { cn } from "../../utils/classMerger";
import LoadingScreen from "../../screens/LoadingScreen";
import AutopilotSettingsFields, {
    ALL_AUTOPILOT_CHANNELS,
} from "../settings/Autopilot/AutopilotSettingsFields";
import AutoVideoPreferences from "../settings/AutoVideoPreferences/AutoVideoPreferences";
import { Switch } from "../misc/Switch";
import {
    AutopilotResponse,
    StagedAutopilotSettings,
} from "../../types/autopilot";

// First-run MLS setup, shown right after the user saves their MLS agent details: property types
// (Next saves them and re-runs discovery), listings to import, then Auto Pilot / Auto-Video when
// offered. Save imports the picks and excludes the rest; closing confirms, then applies defaults.
interface MlsSetupReviewModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // Fired after the final save/close/delete so the parent can refetch listings + counts.
    onChanged?: () => void;
    // The user's current listings (same set shown on the My Listings page) — powers the
    // "My Listings" tab so they can delete an imported listing without leaving setup.
    listings?: AllListings[];
}

const PAGE_SIZE = 4;

type SetupTab = "MyListings" | "Import" | "Excluded";

// An address-hidden row has no street to show; say why.
const primaryLine = (snapshot: MlsCandidateSnapshot): string =>
    snapshot.UnparsedAddress?.trim() ||
    (snapshot.addressUnavailable ? "Address hidden by MLS" : "Address unavailable");

const secondaryLine = (snapshot: MlsCandidateSnapshot): string => {
    const cityState = [snapshot.PostalCity, snapshot.StateOrProvince]
        .filter(Boolean)
        .join(", ");
    return [cityState, snapshot.PostalCode].filter(Boolean).join(" ");
};

// Trestle PropertyType tokens are space-less (e.g. "ResidentialLease"). Split on capitals.
const formatPropertyType = (propertyType: string): string =>
    propertyType
        .replace(/([A-Z])/g, " $1")
        .replace(/,/g, ", ")
        .trim();

// Same meta line the review dialog shows: MLS# · PropertyType · N photos.
const metaLine = (candidate: MlsListingCandidate): string => {
    const parts: string[] = [];
    if (candidate.ListingId) parts.push(`MLS# ${candidate.ListingId}`);
    if (candidate.listingData?.PropertyType) {
        parts.push(
            formatPropertyType(String(candidate.listingData.PropertyType)),
        );
    }
    const photos = candidate.listingData?.PhotosCount;
    if (typeof photos === "number" && photos > 0) {
        parts.push(`${photos} photo${photos === 1 ? "" : "s"}`);
    }
    return parts.join("  ·  ");
};

// My Listings tab rows (already-imported listings) — mirror the review dialog's helpers.
const listingPrimary = (listing: AllListings): string => {
    const addr = listing.formattedAddress?.trim();
    if (!addr) return "Address unavailable";
    const idx = addr.indexOf(",");
    return idx > 0 ? addr.slice(0, idx).trim() : addr;
};

const listingSecondary = (listing: AllListings): string => {
    const addr = listing.formattedAddress?.trim() || "";
    const idx = addr.indexOf(",");
    return idx > 0 ? addr.slice(idx + 1).trim() : "";
};

const listingMetaLine = (listing: AllListings): string => {
    const parts: string[] = [];
    if (listing.mlsId) parts.push(`MLS# ${listing.mlsId}`);
    if (listing.mlsPropertyType) parts.push(formatPropertyType(listing.mlsPropertyType));
    if (typeof listing.imageCount === "number" && listing.imageCount > 0) {
        parts.push(
            `${listing.imageCount} photo${listing.imageCount === 1 ? "" : "s"}`,
        );
    }
    return parts.join("  ·  ");
};

const MlsSetupReviewModal: React.FC<MlsSetupReviewModalProps> = ({
    open,
    onOpenChange,
    onChanged,
    listings = [],
}) => {
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    // MB-1398: autopilot chain — populated only when the user is eligible and
    // autopilot is off; null hides Steps 3-4 entirely (Starter users and
    // already-enabled users keep the 2-step wizard exactly as before).
    const [apData, setApData] = useState<AutopilotResponse | null>(null);
    const [apStaged, setApStaged] = useState<StagedAutopilotSettings | null>(
        null,
    );
    const [apEnabled, setApEnabled] = useState<boolean>(true);
    // MB-1398: MLS Agent Profile shown in an overlay so the wizard (and all
    // staged choices) survives — no tabs, no navigation.
    const [profileOverlayOpen, setProfileOverlayOpen] = useState(false);
    // Step 4 commits through the wizard footer rather than its own button, so
    // the step has a single Save like every other one.
    const videoSaveRef = useRef<(() => Promise<boolean>) | null>(null);
    const [videoReady, setVideoReady] = useState(false);
    const [tab, setTab] = useState<SetupTab>("Import");
    const [pending, setPending] = useState<MlsListingCandidate[]>([]);
    // Keys staged as excluded locally (moved to the Excluded tab). Nothing is discarded on the
    // server until the final Save — this is purely a working set.
    const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [available, setAvailable] = useState<number>(0);
    // MB-1449: server-side headroom as if autopilot were enabled. The wizard
    // shows slots BEFORE the autopilot enable is saved (the PATCH only fires
    // at Save), so while the autopilot step is checked the cap swaps to this.
    const [availableWithAutopilot, setAvailableWithAutopilot] =
        useState<number>(0);
    const [activeUsed, setActiveUsed] = useState<number>(0);
    const [activeLimit, setActiveLimit] = useState<number>(0);
    const [typeOptions, setTypeOptions] = useState<MlsPropertyTypeOption[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
    const typesAtOpenRef = useRef<Set<string>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState<boolean>(false);
    // My Listings tab delete state (immediate delete, same as the review dialog).
    const [deleteTarget, setDeleteTarget] = useState<AllListings | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const [candidates, types, autopilot] = await Promise.allSettled([
            getMlsCandidates("Pending"),
            getMlsPropertyTypes(),
            (async () => {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get(
                    `${backendUrl}/settings/autopilot`,
                    { headers },
                );
                return data as AutopilotResponse;
            })(),
        ]);
        if (candidates.status === "fulfilled") {
            setPending(candidates.value.candidates);
            setAvailable(candidates.value.available);
            setAvailableWithAutopilot(
                candidates.value.availableWithAutopilot ??
                    candidates.value.available,
            );
            setActiveUsed(candidates.value.activeUsed);
            setActiveLimit(candidates.value.activeLimit);
        } else {
            toast.error(
                <CustomToast
                    message="Failed to load your MLS listings. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        }
        if (types.status === "fulfilled") {
            setTypeOptions(types.value.options);
            setSelectedTypes(new Set(types.value.selected));
            typesAtOpenRef.current = new Set(types.value.selected);
        } else {
            toast.error(
                <CustomToast
                    message="Couldn't load property-type options. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        }
        // MB-1398: offer autopilot in-flow only to eligible users who don't
        // have it on yet. A failed fetch silently skips the steps — the wizard
        // behaves exactly as before.
        if (
            autopilot.status === "fulfilled" &&
            autopilot.value?.eligible &&
            !autopilot.value?.settings?.enabled
        ) {
            const s = autopilot.value.settings;
            setApData(autopilot.value);
            setApStaged({
                enabled: true,
                mode: s.mode,
                channels: s.channels || [],
                openHouseSync: s.openHouseSync,
                frontOfProperty: s.frontOfProperty,
                floorPlanPosts: s.floorPlanPosts,
                ctaPlacement: s.ctaPlacement,
                listingPriority: s.listingPriority,
                commercialUsePrice: s.commercialUsePrice,
            });
        } else {
            setApData(null);
            setApStaged(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!open) {
            // Clear in-progress delete state so a dialog dismissed while the delete confirm
            // was open doesn't re-show that irreversible confirm the next time it opens.
            setDeleteTarget(null);
            setDeleting(false);
            return;
        }
        setStep(1);
        setTab("Import");
        setSelected(new Set());
        setExcludedKeys(new Set());
        setPage(1);
        setConfirmCloseOpen(false);
        setApEnabled(true);
        loadAll();
    }, [open, loadAll]);

    // Reset paging when the tab or step changes so we never land on an out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [tab, step]);

    const availableList = useMemo(
        () => pending.filter((c) => !excludedKeys.has(c.ListingKey)),
        [pending, excludedKeys],
    );
    const excludedList = useMemo(
        () => pending.filter((c) => excludedKeys.has(c.ListingKey)),
        [pending, excludedKeys],
    );
    // No discovered listings to import → skip the listing step, show only property types.
    // MB-1430: a blocked candidate can never be imported, so it must not be
    // what keeps the step alive - an agent whose whole discovered book is
    // blocked would otherwise be walked through a step where nothing is
    // selectable and the import does nothing.
    const hasImports = useMemo(
        () => pending.some((c) => !isImportBlocked(c.listingData)),
        [pending],
    );

    const candidateList = tab === "Excluded" ? excludedList : availableList;
    const currentCount =
        tab === "MyListings" ? listings.length : candidateList.length;
    const totalPages = Math.max(1, Math.ceil(currentCount / PAGE_SIZE));
    const candidatePageItems = useMemo(
        () => candidateList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [candidateList, page],
    );
    const listingPageItems = useMemo(
        () => listings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [listings, page],
    );

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    // MB-1449: while the wizard's autopilot step is offered AND checked, the
    // import cap is the with-autopilot headroom; unchecking drops it live.
    // Already-enabled users have apData === null and `available` is already
    // the autopilot-floored number server-side. Same predicate as the Save
    // path's enableAutopilot so the shown cap can never diverge from what
    // Save will actually do.
    const autopilotArmed =
        apData !== null && apStaged !== null && apEnabled;
    const effectiveAvailable = autopilotArmed
        ? availableWithAutopilot
        : available;
    const atCap = effectiveAvailable <= 0;
    const overCap = selected.size > effectiveAvailable;

    const toggle = useCallback((listingKey: string, isSelected: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (isSelected) next.add(listingKey);
            else next.delete(listingKey);
            return next;
        });
    }, []);

    const handleExcludeSelected = useCallback(() => {
        if (selected.size === 0) return;
        setExcludedKeys((prev) => {
            const next = new Set(prev);
            selected.forEach((key) => next.add(key));
            return next;
        });
        setSelected(new Set());
    }, [selected]);

    const handleReadd = useCallback((listingKey: string) => {
        setExcludedKeys((prev) => {
            const next = new Set(prev);
            next.delete(listingKey);
            return next;
        });
    }, []);

    const handleTypeToggle = useCallback((token: string) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(token)) next.delete(token);
            else next.add(token);
            return next;
        });
    }, []);

    // Final commit: import the checked listings, exclude everything else, save the property-type
    // preference — all at once (staged choices are only applied here).
    const handleSave = async (saveVideoPrefs = false, candidates = pending) => {
        const enableAutopilot =
            apData !== null && apStaged !== null && apEnabled;
        setSubmitting(true);
        try {
            // Branding first (MB-1398 apply order). A validation failure leaves
            // the user on the step with nothing imported and autopilot untouched
            // — the form has already said what is missing.
            if (saveVideoPrefs && videoSaveRef.current) {
                const saved = await videoSaveRef.current();
                if (!saved) return;
            }

            await updateMlsPropertyTypes(Array.from(selectedTypes));

            // MB-1398: autopilot settings BEFORE the import so campaigns are
            // born configured (autoIngestEnabled was set before this wizard
            // opened, so the enable prerequisite holds). A rejected enable
            // aborts the whole Save — nothing has been imported yet.
            if (enableAutopilot && apStaged) {
                try {
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    await axios.patch(
                        `${backendUrl}/settings/autopilot`,
                        { ...apStaged, enabled: true },
                        { headers },
                    );
                    // The listings page is mounted behind this wizard with the
                    // pre-enable answer; without this it keeps offering manual
                    // campaign creation until a reload.
                    publishAutopilotEnabled(true);
                } catch (error) {
                    const message =
                        (axios.isAxiosError(error) &&
                            error.response?.data?.message) ||
                        "Couldn't enable Auto Pilot. Please try again.";
                    toast.error(
                        <CustomToast
                            message={String(message)}
                            type="error"
                        />,
                        { autoClose: 4000 },
                    );
                    return;
                }
            }

            let addedCount = 0;
            if (selected.size > 0) {
                const result = await addMlsCandidates(
                    Array.from(selected),
                );
                addedCount = result.added.length;
                // MB-1611: adds clicked while the MLS feed is down are queued and run
                // on their own once it is back.
                const queuedCount = result.queued?.length ?? 0;
                if (queuedCount > 0) {
                    toast.info(
                        <CustomToast
                            message={`${queuedCount} listing${
                                queuedCount === 1 ? "" : "s"
                            } queued — the MLS feed is temporarily unavailable; ${
                                queuedCount === 1 ? "it" : "they"
                            } will be added automatically when it's back.`}
                            type="success"
                        />,
                        { autoClose: 6000 },
                    );
                }
                if (result.rejected.length > 0 || result.failed.length > 0) {
                    const blocked =
                        result.rejected.length + result.failed.length;
                    toast.error(
                        <CustomToast
                            message={`${blocked} listing${
                                blocked === 1 ? "" : "s"
                            } couldn't be added (active-listing limit reached).`}
                            type="error"
                        />,
                        { autoClose: 4000 },
                    );
                }
            }
            const discardKeys = candidates
                .map((candidate) => candidate.ListingKey)
                .filter((key) => !selected.has(key));
            if (discardKeys.length > 0) {
                try {
                    await discardMlsCandidates(discardKeys);
                } catch {
                    // best-effort — leftover Pending stays in the normal review list.
                }
            }
            // MB-1398: sweep immediately so already-ingested listings get
            // campaigns now; freshly imported ones follow as each finishes
            // ingest (per-listing nudge + lock-busy retry server-side).
            if (enableAutopilot) {
                try {
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    await axios.post(
                        `${backendUrl}/autopilot/reconcile`,
                        {},
                        { headers },
                    );
                } catch {
                    // Non-fatal: the ingest nudge and daily sweep cover it.
                }
            }

            // One press, one confirmation. The import, the auto-video save and
            // the autopilot enable used to each announce themselves, leaving the
            // user to read a stack of three toasts to find out if anything
            // failed. Errors above stay separate — those do need to stand out.
            const listingsPart =
                addedCount > 0
                    ? `${addedCount} listing${addedCount === 1 ? "" : "s"} added`
                    : "";
            const autopilotPart = enableAutopilot
                ? "Auto Pilot is on — campaigns are being created"
                : "";
            const summary =
                [listingsPart, autopilotPart].filter(Boolean).join(". ") ||
                "Setup saved";
            toast.success(
                <CustomToast message={`${summary}.`} type="success" />,
                { autoClose: 4000 },
            );

            onChanged?.();
            onOpenChange(false);
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't save. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Delete an already-imported listing from the My Listings tab (same as the review dialog).
    const handleDeleteListing = async () => {
        const target = deleteTarget;
        if (!target) return;
        setDeleteTarget(null);
        setDeleting(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(
                `${backendUrl}/listing/delete`,
                { listingId: target.id, listingType: target.listingType },
                { headers },
            );
            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Listing deleted successfully."
                        type="success"
                    />,
                    { autoClose: 3000 },
                );
                onChanged?.();
            } else {
                throw new Error("Failed to delete listing.");
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to delete listing. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setDeleting(false);
        }
    };

    const requestClose = useCallback(() => {
        setConfirmCloseOpen(true);
    }, []);

    const handleConfirmClose = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const discardKeys = pending
                .map((candidate) => candidate.ListingKey)
                .filter((key) => !selected.has(key));
            if (discardKeys.length > 0) {
                await discardMlsCandidates(discardKeys);
            }
            onChanged?.();
            setConfirmCloseOpen(false);
            onOpenChange(false);
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't finish setup. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setSubmitting(false);
        }
    };

    const renderCandidateRow = (candidate: MlsListingCandidate) => {
        const snapshot = candidate.listingData ?? {};
        const isExcludedTab = tab === "Excluded";
        const isSelected = selected.has(candidate.ListingKey);
        // MB-1430: the MLS forbids this listing on the internet - visible here
        // so the agent knows it exists, but never importable.
        const importBlocked = isImportBlocked(snapshot);
        const blockedTooltip = importBlockedTooltip();
        // The MLS hides the address: importable, marketed as "City, ST ZIP";
        // the chip says so.
        const addressHidden = isAddressHidden(snapshot);
        const dupHint = duplicateHint(candidate);

        return (
            <div
                key={candidate.ListingKey}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-secondary hover:bg-gray-50 min-[480px]:flex-row min-[480px]:items-center"
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    {!isExcludedTab && (
                        <SimpleTooltip
                            message="This listing can't be imported"
                            visible={importBlocked}
                            className="shrink-0"
                        >
                            <span
                                className={
                                    importBlocked
                                        ? "pointer-events-none"
                                        : undefined
                                }
                            >
                                <Checkbox
                                    variant="radix"
                                    disabled={importBlocked}
                                    checked={isSelected && !importBlocked}
                                    onChange={(value) => {
                                        if (!importBlocked) {
                                            toggle(candidate.ListingKey, value);
                                        }
                                    }}
                                    id={`setup-cand-${candidate.ListingKey}`}
                                />
                            </span>
                        </SimpleTooltip>
                    )}

                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2 text-primary">
                            <Home className="h-4 w-4 shrink-0 text-[#e9c468]" />
                            <span className="truncate text-sm font-semibold">
                                {primaryLine(snapshot)}
                            </span>
                        </div>
                        {secondaryLine(snapshot) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                    {secondaryLine(snapshot)}
                                </span>
                            </div>
                        )}
                        {(metaLine(candidate) || importBlocked || addressHidden) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-gray-400">
                                {metaLine(candidate) && (
                                    <span className="truncate">
                                        {metaLine(candidate)}
                                    </span>
                                )}
                                {importBlocked && (
                                    <SimpleTooltip
                                        message={blockedTooltip}
                                        visible
                                        className="shrink-0"
                                        tooltipClassName="bg-secondary text-primary"
                                    >
                                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#e9c468] px-2 py-0.5 text-[11px] font-medium text-[#193456]">
                                            <Info className="h-3 w-3" />
                                            Can't be imported
                                        </span>
                                    </SimpleTooltip>
                                )}
                                {addressHidden && !importBlocked && (
                                    <SimpleTooltip
                                        message={addressHiddenTooltip(snapshot)}
                                        visible
                                        className="shrink-0"
                                        tooltipClassName="bg-secondary text-primary"
                                    >
                                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                                            <Info className="h-3 w-3" />
                                            Address hidden
                                        </span>
                                    </SimpleTooltip>
                                )}
                            </div>
                        )}
                        {dupHint && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
                                <Info className="h-3 w-3 shrink-0" aria-hidden />
                                <span>{dupHint}</span>
                            </p>
                        )}
                    </div>
                </div>

                {isExcludedTab && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={submitting}
                        onClick={() => handleReadd(candidate.ListingKey)}
                        className="w-full shrink-0 min-[480px]:w-auto"
                    >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Re-add
                    </Button>
                )}
            </div>
        );
    };

    const renderListingRow = (listing: AllListings) => {
        const canDelete = listing.status !== "Processing" && !listing.isSyncing;
        const deleteBlockedByCampaign = listing.campaignStatus === "PROCESSING";
        const secondary = listingSecondary(listing);
        const meta = listingMetaLine(listing);
        const statusLabel = listing.status
            ?.replace(/([A-Z])/g, " $1")
            .trim();

        return (
            <div
                key={listing.id}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-secondary hover:bg-gray-50 min-[480px]:flex-row min-[480px]:items-center"
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2 text-primary">
                            <Home className="h-4 w-4 shrink-0 text-[#e9c468]" />
                            <span className="truncate text-sm font-semibold">
                                {listingPrimary(listing)}
                            </span>
                        </div>
                        {secondary && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">{secondary}</span>
                            </div>
                        )}
                        {(meta || statusLabel) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-gray-400">
                                {meta && <span className="truncate">{meta}</span>}
                                {statusLabel && (
                                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                                        {statusLabel}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {canDelete && (
                    <SimpleTooltip
                        message="Please wait for the campaign to finish before deleting the listing"
                        visible={deleteBlockedByCampaign}
                        className="w-full shrink-0 min-[480px]:w-auto"
                    >
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={deleting || deleteBlockedByCampaign}
                            onClick={() => {
                                if (!deleteBlockedByCampaign) {
                                    setDeleteTarget(listing);
                                }
                            }}
                            className="w-full min-[480px]:w-auto"
                        >
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                        </Button>
                    </SimpleTooltip>
                )}
            </div>
        );
    };

    const emptyState = (label: string) => (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400">
            <Inbox className="h-10 w-10" />
            <p className="text-sm">{label}</p>
        </div>
    );

    const picker = (
        <div>
            <p className="text-sm font-semibold text-primary">
                Auto-Sync Property Types
            </p>
            <p className="mb-4 mt-1 text-xs leading-relaxed text-gray-500">
                New listings of the selected types are added automatically; the
                rest go to Excluded for review.
            </p>
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
                {typeOptions.map((option) => (
                    <Checkbox
                        key={option.token}
                        variant="radix"
                        id={`setup-ptype-${option.token}`}
                        label={option.label}
                        checked={selectedTypes.has(option.token)}
                        onChange={() => handleTypeToggle(option.token)}
                    />
                ))}
            </div>
        </div>
    );

    // Step indicator: equal-share columns (circle + label centered under it), so
    // the circle centres always sit at (2i-1)/2N across the track whatever the
    // container width — the connector below is inset by exactly that. The old
    // fixed w-28 columns only lined up at two steps in a 15rem box; at four they
    // overflowed, shrank, and left the line short of the outer circles.
    const stepColumn = (index: 1 | 2 | 3 | 4, label: string) => {
        const active = step === index;
        const done = step > index;
        return (
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <span
                    className={cn(
                        "relative z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-white",
                        done
                            ? "border-secondary"
                            : active
                              ? "border-primary"
                              : "border-gray-300",
                    )}
                >
                    {(active || done) && (
                        <span
                            className={cn(
                                "h-2 w-2 rounded-full",
                                done ? "bg-secondary" : "bg-primary",
                            )}
                        />
                    )}
                </span>
                <span
                    className={cn(
                        "text-center text-xs font-medium",
                        step >= index ? "text-primary" : "text-gray-400",
                    )}
                >
                    {label}
                </span>
            </div>
        );
    };

    const showAutopilotStep = apData !== null && apStaged !== null;
    // Auto-Video is Autopilot's step: it exists so the fleet Autopilot is about
    // to create is born with the right look. Decline Autopilot and no campaign
    // is created here at all — the video setup then belongs to the campaign
    // wizard, which carries its own AutoVideoPreferences per campaign.
    const showVideoStep = showAutopilotStep && apEnabled;

    // Inset to the first/last circle centre, and how much of the track the
    // completed steps cover. Inline styles because Tailwind cannot generate
    // arbitrary values from a runtime expression.
    const stepCount = showVideoStep ? 4 : showAutopilotStep ? 3 : 2;
    const trackInset = `${100 / (stepCount * 2)}%`;
    const trackProgress = (step - 1) / (stepCount - 1);

    const stepper = (
        <div
            className={cn(
                "relative mx-auto mt-2 w-full",
                showVideoStep
                    ? "max-w-[26rem]"
                    : showAutopilotStep
                      ? "max-w-[21rem]"
                      : "max-w-[15rem]",
            )}
        >
            <div
                className="absolute top-[9px] h-0.5 rounded bg-gray-200"
                style={{ left: trackInset, right: trackInset }}
            />
            <div
                className="absolute top-[9px] h-0.5 rounded bg-secondary"
                style={{
                    left: trackInset,
                    width: `calc((100% - ${trackInset} * 2) * ${trackProgress})`,
                }}
            />
            <div className="flex justify-between">
                {stepColumn(1, SETUP_STEP_LABELS[0])}
                {stepColumn(2, SETUP_STEP_LABELS[1])}
                {showAutopilotStep && stepColumn(3, "Auto Pilot")}
                {showVideoStep && stepColumn(4, "Auto-Video")}
            </div>
        </div>
    );

    // MB-1398 Step 3: the full Settings → Autopilot surface, staged locally
    // and applied at the final Save (settings land before any import).
    const autopilotStepContent =
        showAutopilotStep && apStaged ? (
            <div className="space-y-6">
                <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
                    <div className="flex flex-col flex-[0.8]">
                        <span className="font-semibold text-primary">
                            Enable Auto Pilot
                        </span>
                        <span className="mt-1 text-sm text-gray-500">
                            Auto Pilot will market your active listings and
                            every new listing that syncs in. Posts publish
                            automatically without approval. Eligible manual
                            campaigns switch over automatically, and listings
                            added manually before MLS access are removed -
                            your listings come in from your MLS instead.
                        </span>
                    </div>
                    <Switch
                        id="wizard-autopilot-enabled"
                        checked={apEnabled}
                        onClick={() => setApEnabled(!apEnabled)}
                    />
                </div>
                {apEnabled && (
                    <AutopilotSettingsFields
                        staged={apStaged}
                        onChange={setApStaged}
                        connectedChannels={apData.connectedChannels || []}
                        unconnectedChannels={ALL_AUTOPILOT_CHANNELS.filter(
                            (channel) =>
                                !(apData.connectedChannels || []).includes(
                                    channel,
                                ),
                        )}
                        disabled={submitting}
                        hideEnableSwitch
                        showConnectionsLink={false}
                    />
                )}
            </div>
        ) : null;

    // MB-1398 Step 4: the existing Listing Video Settings form, committed by
    // this wizard's footer. Offered whether or not autopilot was enabled —
    // campaign renewal and manual campaigns read the same settings.
    const videoStepContent = (
        <div className="space-y-4">
            <p className="text-sm text-gray-500">
                Marabot uses these Listing Video Settings whenever it creates a
                video for your listings. Set the look once here — template,
                colors, font, logo, and music — or skip and tune it later from
                Settings.
            </p>
            <AutoVideoPreferences
                embedded
                hideSaveButton
                saveRef={videoSaveRef}
                onReadyChange={setVideoReady}
            />
        </div>
    );

    // The API rejects enabling with no channels (UpdateAutopilotSettingsDto
    // @ArrayMinSize(1)), but only at the final Save — by then the user has filled
    // in Auto-Video and the whole import aborts having created nothing. Say it on
    // the step that can fix it, with the server's own wording.
    const autopilotChannelsMissing =
        apEnabled && (apStaged?.channels?.length ?? 0) === 0;

    // The discovery that ran before this wizard opened used the default types,
    // so opt-in commercial listings cannot be in the list yet: save the chosen
    // types and re-run discovery before offering the listings.
    const advanceFromTypes = async () => {
        // Same rule as Settings > MLS Agent Profile: auto-syncing zero property
        // types is a no-op, so the lookup below never runs on an empty pick.
        if (selectedTypes.size === 0) {
            toast.error(
                <CustomToast
                    message="Select at least one property type to auto-sync"
                    type="error"
                />,
                { autoClose: 3000 },
            );
            return;
        }
        setSubmitting(true);
        let fresh = pending;
        let refreshed = false;
        try {
            await updateMlsPropertyTypes(Array.from(selectedTypes));
            await refreshMlsCandidates();
            const excluded = await getMlsCandidates("Discarded");
            const restoreKeys = keysToRestore(
                excluded.candidates,
                selectedTypes,
                typesAtOpenRef.current,
            );
            if (restoreKeys.length > 0) {
                await restoreMlsCandidates(restoreKeys);
            }
            const latest = await getMlsCandidates("Pending");
            fresh = latest.candidates;
            setPending(fresh);
            setAvailable(latest.available);
            setAvailableWithAutopilot(
                latest.availableWithAutopilot ?? latest.available,
            );
            setActiveUsed(latest.activeUsed);
            setActiveLimit(latest.activeLimit);
            setSelected((prev) => keepKnownKeys(prev, fresh));
            setExcludedKeys((prev) => keepKnownKeys(prev, fresh));
            setTab("Import");
            setPage(1);
            refreshed = true;
        } catch {
            // Non-fatal: continue with the listings discovered before the
            // wizard opened; the 15-minute sync picks up the rest.
            toast.error(
                <CustomToast
                    message="Couldn't look up listings for these property types. Continuing with the ones found so far."
                    type="error"
                />,
                { autoClose: 4000 },
            );
        } finally {
            setSubmitting(false);
        }
        const imports = fresh.some((c) => !isImportBlocked(c.listingData));
        const next = stepAfterTypes({ hasImports: imports, showAutopilotStep });
        if (!imports && refreshed) {
            toast.info(
                <CustomToast
                    message="No new listings to import for the property types you chose."
                    type="info"
                />,
                { autoClose: 4000 },
            );
        }
        if (next === null) {
            // State set above is not visible to this closure yet.
            await handleSave(false, fresh);
            return;
        }
        setStep(next);
    };
    const advanceFromAutopilot = () => {
        // MB-1449: the step-1 gate ran against the with-autopilot cap. If the
        // user turned Autopilot off here, the manual cap is lower — re-check
        // before committing, or the server clamps the import and silently
        // discards the rest of the selection.
        if (!apEnabled && selected.size > available) {
            toast.error(
                <CustomToast
                    message={`Without Auto Pilot you can import up to ${available} listing${available === 1 ? "" : "s"} on your plan — unselect ${selected.size - available} to continue.`}
                    type="error"
                />,
                { autoClose: 6000 },
            );
            setStep(2);
            return;
        }
        if (autopilotChannelsMissing) {
            toast.error(
                <CustomToast
                    message="Auto Pilot needs at least one channel to post to."
                    type="error"
                />,
                { autoClose: 4000 },
            );
            return;
        }
        if (showVideoStep) setStep(4);
        else handleSave();
    };

    const autopilotFooter = (
        <div className="flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
            <Button
                variant="outline"
                disabled={submitting}
                onClick={() => setStep(stepBeforeAutopilot({ hasImports }))}
            >
                Back
            </Button>
            {/* The Enable Autopilot switch above is the opt-out; a separate
                skip button here would be the same decision twice. With it off
                there is no Auto-Video step to advance to, so this commits. */}
            <Button disabled={submitting} onClick={advanceFromAutopilot}>
                {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {showVideoStep ? "Next" : "Save"}
            </Button>
        </div>
    );

    const videoFooter = (
        <div className="flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
            <Button
                variant="outline"
                disabled={submitting}
                onClick={() => setStep(3)}
            >
                Back
            </Button>
            <Button
                variant="outline"
                disabled={submitting}
                onClick={() => handleSave()}
            >
                Skip & Finish
            </Button>
            {/* Disabled until the video form reports it has loaded — saving
                sooner would commit its defaults. Skip & Finish stays available. */}
            <Button
                disabled={submitting || !videoReady}
                onClick={() => handleSave(true)}
            >
                {submitting && (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                Save & Finish
            </Button>
        </div>
    );

    // Until the candidates + property types have loaded, show the brand 3-dot loader
    // rather than an empty dialog shell, so the dialog only appears once it's populated.
    if (open && loading) {
        return <LoadingScreen />;
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}
        >
            <DialogContent
                className="relative flex h-[85dvh] max-h-[calc(94dvh-2rem)] w-full max-w-2xl flex-col overflow-y-hidden bg-white p-0 min-[640px]:h-[48rem]"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <div className="shrink-0 border-b border-gray-100 p-4 pb-3 min-[640px]:p-6 min-[640px]:pb-4">
                    <div className="flex items-start justify-between gap-4">
                        <DialogTitle>Set up MLS Auto-Sync</DialogTitle>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={requestClose}
                            className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Shown from step 1: an empty listings step is only known after the refresh. */}
                    {!loading && stepper}

                    {!loading && (
                        <p className="mt-2 text-sm text-gray-500">
                            {step === 1
                                ? "Choose which property types Marabot should sync from your MLS. Next, we look up your active listings of those types so you can pick which to import. You can change this anytime from the Settings page."
                                : step === 2
                                  ? "We found active listings associated with your MLS Agent ID. Select the ones you'd like to import; set aside any you don't want (re-add anytime from the Excluded tab)."
                                  : step === 3
                                      ? "Auto Pilot markets every active listing automatically. Turn it on now and tune how it posts — or skip and enable it later from Settings."
                                      : "Set up the look of your automatic listing videos. Optional — you can change these anytime from Settings."}
                        </p>
                    )}

                    {!loading && hasImports && step === 2 && (
                        <Tabs
                            value={tab}
                            onValueChange={(value) => setTab(value as SetupTab)}
                            className="mt-3"
                        >
                            <TabsList className="w-full flex-nowrap gap-1 p-1.5 min-[640px]:gap-2 min-[640px]:p-2">
                                <TabsTrigger
                                    value="MyListings"
                                    className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                                >
                                    My Listings ({listings.length})
                                </TabsTrigger>
                                <TabsTrigger
                                    value="Import"
                                    className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                                >
                                    <span className="min-[640px]:hidden">
                                        To Import ({availableList.length})
                                    </span>
                                    <span className="hidden min-[640px]:inline">
                                        Available to Import ({availableList.length})
                                    </span>
                                </TabsTrigger>
                                <TabsTrigger
                                    value="Excluded"
                                    className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                                >
                                    Excluded ({excludedList.length})
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 min-[640px]:px-6 min-[640px]:py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-primary">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : step === 3 ? (
                        autopilotStepContent
                    ) : step === 4 ? (
                        videoStepContent
                    ) : step === 1 ? (
                        picker
                    ) : tab === "MyListings" ? (
                        <div className="space-y-2">
                            {listings.length === 0
                                ? emptyState("You don't have any listings yet.")
                                : listingPageItems.map(renderListingRow)}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {candidateList.length === 0
                                ? emptyState(
                                      tab === "Import"
                                          ? "No listings to import right now."
                                          : "Nothing set aside.",
                                  )
                                : candidatePageItems.map(renderCandidateRow)}
                        </div>
                    )}

                    {!loading && hasImports && step === 2 && totalPages > 1 && (
                        <Pagination className="mt-4">
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPage((p) => Math.max(1, p - 1));
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem>
                                    <span className="px-3 text-sm text-primary">
                                        Page {page} of {totalPages}
                                    </span>
                                </PaginationItem>
                                <PaginationItem>
                                    <PaginationNext
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPage((p) =>
                                                Math.min(totalPages, p + 1),
                                            );
                                        }}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    )}
                </div>

                {!loading && (
                    <div className="shrink-0 border-t border-gray-100 p-4">
                        {step === 3 ? (
                            autopilotFooter
                        ) : step === 4 ? (
                            videoFooter
                        ) : step === 2 ? (
                            <>
                                <div className="mb-2 text-center text-xs text-gray-500">
                                    {atCap ? (
                                        <span className="text-[#d23600]">
                                            You&apos;ve reached the active listing
                                            limit for your plan — set aside the
                                            ones you don&apos;t want, or continue.
                                        </span>
                                    ) : overCap ? (
                                        <span className="text-[#d23600]">
                                            You can import up to{" "}
                                            {effectiveAvailable} on your current
                                            plan — unselect{" "}
                                            {selected.size - effectiveAvailable}{" "}
                                            to continue.
                                        </span>
                                    ) : (
                                        <span>
                                            {selected.size} of{" "}
                                            {effectiveAvailable} slot
                                            {effectiveAvailable === 1
                                                ? ""
                                                : "s"}{" "}
                                            selected
                                            {autopilotArmed
                                                ? ` · ${activeUsed} active now`
                                                : activeLimit > 0
                                                  ? ` · ${activeUsed} of ${activeLimit} used`
                                                  : ""}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
                                    <Button
                                        variant="outline"
                                        disabled={submitting}
                                        className="min-[480px]:mr-auto"
                                        onClick={() => setStep(1)}
                                    >
                                        Back
                                    </Button>
                                    {tab === "Import" && (
                                        <Button
                                            variant="outline"
                                            disabled={
                                                submitting || selected.size === 0
                                            }
                                            onClick={handleExcludeSelected}
                                        >
                                            Exclude Selected
                                        </Button>
                                    )}
                                    <Button
                                        disabled={submitting || overCap}
                                        onClick={() => {
                                            const next = stepAfterListings({
                                                showAutopilotStep,
                                            });
                                            if (next === null) handleSave();
                                            else setStep(next);
                                        }}
                                    >
                                        {submitting && (
                                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                        )}
                                        {selected.size > 0
                                            ? `Add Selected & ${showAutopilotStep ? "Next" : "Save"}`
                                            : `Skip & ${showAutopilotStep ? "Next" : "Save"}`}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
                                <Button
                                    variant="ghost"
                                    disabled={submitting}
                                    className="min-[480px]:mr-auto"
                                    onClick={() => setProfileOverlayOpen(true)}
                                >
                                    View MLS Agent Profile
                                </Button>
                                <Button
                                    disabled={submitting}
                                    onClick={() => advanceFromTypes()}
                                >
                                    {submitting && (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    )}
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {deleting && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/70">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                )}

                {/* MB-1398: agent profile overlay — layered dialog, wizard
                    stays mounted underneath, closing returns exactly here. */}
                <Dialog
                    open={profileOverlayOpen}
                    onOpenChange={setProfileOverlayOpen}
                >
                    <DialogContent className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto bg-white p-2">
                        <DialogTitle className="sr-only">
                            MLS Agent Profile
                        </DialogTitle>
                        <MlsDetails
                            embedded
                            onClose={() => setProfileOverlayOpen(false)}
                        />
                    </DialogContent>
                </Dialog>

                <ReconfirmOverlay
                    isOpen={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDeleteListing}
                    title="Delete Listing"
                    message="Are you sure you want to delete this listing?"
                    messageTwo="This action cannot be undone."
                    confirmText="Delete Listing"
                    cancelText="Cancel"
                    reverse={true}
                />

                <ConfirmPopup
                    open={confirmCloseOpen}
                    title="Leave MLS Setup?"
                    message="Any listings you haven't added will be skipped and default auto-sync property types applied. You can add listings and update these preferences anytime from Settings."
                    confirmText="Leave setup"
                    cancelText="Keep editing"
                    confirmVariant="destructive"
                    onConfirm={handleConfirmClose}
                    onCancel={() => setConfirmCloseOpen(false)}
                    cancelDisabled={submitting}
                />
            </DialogContent>
        </Dialog>
    );
};

export default MlsSetupReviewModal;
